import { withError } from '../common-utils';
import { prefix } from './logger';

const { error } = prefix('WebGLFilter');

const glCanvas = document.createElement('canvas');
const glContext = glCanvas.getContext('webgl')!;
const VERTEX_SHADER_SOURCE = `attribute vec2 a_position;
varying vec2 v_texCoord;
void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
  // 将坐标从 [-1, 1] 转换为 [0, 1] 作为纹理坐标
  v_texCoord = a_position * 0.5 + 0.5;
}
`;
const maxTextureSize = glContext.getParameter(glContext.MAX_TEXTURE_SIZE);

type ProgramWithLocations = WebGLProgram & {
  locations: Record<string, number | WebGLUniformLocation>;
};

export const glHelper: {
  gl: WebGLRenderingContext;
  glCanvas: HTMLCanvasElement;
  maxTextureSize: number;
  vertexShader: WebGLShader | null;
  quadBuffer: WebGLBuffer | null;
  programCache: Map<string, ProgramWithLocations>;
  compileShader(type: GLenum, source: string): WebGLShader;
  getVertexShader(): WebGLShader;
  getProgram(fragmentSource: string, name: string): ProgramWithLocations;
  setupQuad(program: ProgramWithLocations): void;
  createTexture(sourceCanvas: HTMLCanvasElement): WebGLTexture | null;
  setUniform(location: WebGLUniformLocation, value: any): void;
  setupTexture(program: ProgramWithLocations, targetCanvas: HTMLCanvasElement): () => void;
  setupGlCanvas(targetCanvas: HTMLCanvasElement): void;
} = {
  gl: glContext,
  glCanvas: glCanvas,
  maxTextureSize,
  vertexShader: null,
  quadBuffer: null,
  programCache: new Map(),

  getVertexShader() {
    if (this.vertexShader) {
      return this.vertexShader;
    }

    const vertexShader = this.compileShader(this.gl.VERTEX_SHADER, VERTEX_SHADER_SOURCE);
    this.vertexShader = vertexShader;
    return vertexShader;
  },

  compileShader(type: GLenum, source: string) {
    const gl = this.gl;
    const shader = gl.createShader(type);

    if (!shader) {
      throw new Error('Failed to create shader.');
    }

    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      gl.deleteShader(shader);
      throw new Error(`Failed to compile shader: ${gl.getShaderInfoLog(shader) ?? ''}`);
    }
    return shader;
  },

  /**
   * Create WebGL Program
   * @param fragmentSource The source code of the fragment shader.
   * @param name The name of the filter, used to cache the program so it should be unique.
   */
  getProgram(fragmentSource: string, name: string) {
    if (this.programCache.has(name)) {
      return this.programCache.get(name)!;
    }

    const gl = this.gl;
    const vertexShader = this.getVertexShader();
    const fragmentShader = this.compileShader(gl.FRAGMENT_SHADER, fragmentSource);
    const program = gl.createProgram() as WebGLProgram & {
      locations: Record<string, number | WebGLUniformLocation>;
    };

    if (!program) {
      throw new Error(`Failed to create WebGL program.`);
    }
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      gl.deleteProgram(program);
      gl.deleteShader(fragmentShader);
      throw new Error(`Failed to link WebGL program: ${gl.getProgramInfoLog(program)}`);
    }

    gl.deleteShader(fragmentShader);

    program.locations = {};

    // 1. 缓存 Attributes
    const attribCount = gl.getProgramParameter(program, gl.ACTIVE_ATTRIBUTES);
    for (let i = 0; i < attribCount; i++) {
      const attribInfo = gl.getActiveAttrib(program, i);
      if (!attribInfo) {
        throw new Error(`Failed to get active attribute at index ${i}.`);
      }
      program.locations[attribInfo.name] = gl.getAttribLocation(program, attribInfo.name);
    }

    const uniformCount = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS);
    for (let i = 0; i < uniformCount; i++) {
      const uniformInfo = gl.getActiveUniform(program, i);
      if (!uniformInfo) {
        throw new Error(`Failed to get active uniform at index ${i}.`);
      }
      const uniformLoc = gl.getUniformLocation(program, uniformInfo.name);
      if (uniformLoc === null) {
        throw new Error(`Failed to get location of uniform ${uniformInfo.name}.`);
      }
      program.locations[uniformInfo.name] = uniformLoc;
    }

    this.programCache.set(name, program);
    return program;
  },

  setupQuad(program: ProgramWithLocations) {
    const gl = this.gl;
    if (!this.quadBuffer) {
      this.quadBuffer = gl.createBuffer();

      gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer);
      gl.bufferData(
        gl.ARRAY_BUFFER,
        new Float32Array([-1.0, -1.0, 1.0, -1.0, -1.0, 1.0, -1.0, 1.0, 1.0, -1.0, 1.0, 1.0]),
        gl.STATIC_DRAW,
      );
    }

    gl.enableVertexAttribArray(program.locations['a_position'] as GLuint);
    gl.vertexAttribPointer(program.locations['a_position'] as GLuint, 2, gl.FLOAT, false, 0, 0);
  },

  /**
   * 创建并设置一个纹理
   * @param {HTMLCanvasElement} sourceCanvas
   */
  createTexture(sourceCanvas) {
    const gl = this.gl;
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, sourceCanvas);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    return texture;
  },

  setUniform(location: string, value: number | number[]) {
    const gl = this.gl;
    if (typeof value === 'number') {
      gl.uniform1f(location, value);
    } else if (Array.isArray(value)) {
      switch (value.length) {
        case 2:
          gl.uniform2fv(location, value);
          break;
        case 3:
          gl.uniform3fv(location, value);
          break;
        case 4:
          gl.uniform4fv(location, value);
          break;
      }
    }
  },

  setupTexture(program: ProgramWithLocations, targetCanvas: HTMLCanvasElement) {
    const gl = this.gl;
    const inputTexture = glHelper.createTexture(targetCanvas);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, inputTexture);

    if (program.locations['u_image']) {
      gl.uniform1i(program.locations.u_image, 0);
    }
    if (program.locations['u_resolution']) {
      gl.uniform2f(program.locations.u_resolution, gl.drawingBufferWidth, gl.drawingBufferHeight);
    }

    return () => {
      gl.deleteTexture(inputTexture);
    };
  },

  setupGlCanvas(targetCanvas: HTMLCanvasElement) {
    const internalCanvas = this.glCanvas;
    const gl = this.gl;
    if (
      internalCanvas.width !== targetCanvas.width ||
      internalCanvas.height !== targetCanvas.height
    ) {
      internalCanvas.width = targetCanvas.width;
      internalCanvas.height = targetCanvas.height;
      gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
    }
  },
};

type Filter = {
  name: string;
  shader: string;
  uniforms: {
    name: string;
  }[];
  pass: (params: Record<string, unknown>) => Record<string, unknown>[];
};

export function supportWebGL(): boolean {
  return !!glContext;
}

const preservedLocations = new Set(['a_position', 'u_image', 'u_resolution']);

/**
 * Apply shader filter on a canvas
 * @param targetCanvas The canvas to apply the shader on. It should be a 2d context canvas.
 */
export function applyShader(
  targetCanvas: HTMLCanvasElement,
  filter: Filter,
  uniforms: Record<string, unknown> = {},
) {
  const { shader } = filter;
  const gl = glHelper.gl;

  const applySingleShader = (
    shaderSource: string,
    name: string,
    sourceCanvas: HTMLCanvasElement,
    uniforms: Record<string, unknown> = {},
  ) => {
    const { err: pErr, result: program } = withError(() => glHelper.getProgram(shaderSource, name));

    if (pErr) {
      error(`[Filter: ${name}] ${pErr.message}`);
      return;
    }

    glHelper.setupGlCanvas(sourceCanvas);

    gl.clearColor(0.0, 0.0, 0.0, 0.0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    gl.useProgram(program);

    glHelper.setupQuad(program);
    const disposeTexture = glHelper.setupTexture(program, sourceCanvas);

    Object.keys(uniforms).forEach((name) => {
      if (preservedLocations.has(name)) {
        return;
      }

      const location = program.locations[name];
      if (location && uniforms[name] !== undefined) {
        glHelper.setUniform(location, uniforms[name]);
      }
    });

    gl.drawArrays(gl.TRIANGLES, 0, 6);

    const result = document.createElement('canvas');
    const ctx = result.getContext('2d')!;
    result.width = sourceCanvas.width;
    result.height = sourceCanvas.height;
    ctx.clearRect(0, 0, sourceCanvas.width, sourceCanvas.height);
    ctx.drawImage(glHelper.glCanvas, 0, 0);

    disposeTexture();

    return result;
  };

  let result;
  if (filter.pass) {
    const passes = filter.pass(uniforms);
    if (passes.length === 0) {
      return;
    }

    result = passes.reduce((pre, passUniforms) => {
      const result = applySingleShader(shader, filter.name, pre, passUniforms);
      return result;
    }, targetCanvas);
  } else {
    result = applySingleShader(shader, filter.name, targetCanvas, uniforms);
  }

  if (result) {
    const ctx = targetCanvas.getContext('2d')!;
    ctx.clearRect(0, 0, targetCanvas.width, targetCanvas.height);
    ctx.drawImage(result, 0, 0);
  }
}
